import { useEffect, useState } from "react";
import { YoutubeLogo } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { VideoCard } from "@/components/VideoStrip";
import { ShareBar } from "@/components/ShareBar";

export default function Videos() {
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState(null);

  useSeo({
    title: "Videos — demos, how-to and updates from the floor | Unity & Utility",
    description: "Watch the latest videos from the Unity & Utility channel: walkthroughs, how-to guides and updates from the yard, the road and the workshop.",
    path: "/videos",
    jsonLd: breadcrumbs([["Home", "/"], ["Videos", "/videos"]]),
  });

  useEffect(() => {
    api.get("/videos/latest?limit=15").then(({ data: d }) => setData(d)).catch(() => setData({ items: [] }));
    api.get("/videos/channel").then(({ data: d }) => setChannel(d)).catch(() => {});
  }, []);

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="videos-page">
        <Crumbs items={[["Home", "/"], ["Videos", null]]} />
        <p className="overline text-[#B3241B]">The channel</p>
        <h1 className="font-display mt-2 text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          {data?.channel_title || "Videos from the floor"}
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
          Walkthroughs, how-to guides and updates from the yard, the road and the workshop.
          Nothing loads from YouTube until you press play.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {channel?.subscribe_url && (
            <a href={channel.subscribe_url} target="_blank" rel="noopener noreferrer" data-testid="videos-subscribe" className="inline-flex items-center gap-2 bg-[#B3241B] px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#8E1C15]">
              <YoutubeLogo size={17} weight="fill" /> Subscribe on YouTube
            </a>
          )}
          <ShareBar url={`${window.location.origin}/videos`} text="Videos from Unity & Utility" testid="videos-share" compact />
        </div>

        {data?.unavailable && (
          <p data-testid="videos-unavailable" className="mt-6 border-l-2 border-[#B3241B] bg-[#FFECEB] px-3 py-2 text-xs text-[#7A1712]">
            YouTube is not reachable just now — try again in a moment.
          </p>
        )}
        {data?.stale && (
          <p data-testid="videos-stale" className="mt-6 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-xs text-[#0F172A]">
            Showing the last list we had — YouTube was not reachable just now.
          </p>
        )}

        {!data ? (
          <p className="py-16 text-center text-sm text-slate-500">Loading…</p>
        ) : data.items.length === 0 ? (
          <p data-testid="videos-empty" className="mt-8 border border-border bg-white p-8 text-center text-sm text-slate-600">
            No videos to show yet.
          </p>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="videos-grid">
            {data.items.map((v) => <VideoCard key={v.video_id} video={v} testid={`video-${v.video_id}`} />)}
          </div>
        )}
      </div>
    </PublicShell>
  );
}
